import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  cacheMenuData,
  getCachedMenuData,
  enqueuePendingOrder,
  listPendingOrders,
  markOrderSynced,
  markOrderFailed,
  countPendingByOutlet,
} from '@/lib/offlineStore';const OfflineContext = createContext(null);

export const useOffline = () => {
  const ctx = useContext(OfflineContext);
  if (!ctx) throw new Error('useOffline must be used within OfflineProvider');
  return ctx;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [simulatedOffline, setSimulatedOffline] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [outletId, setOutletId] = useState(null);
  const syncingRef = useRef(false);

  const effectiveOnline = isOnline && !simulatedOffline;

  // Listen to browser online/offline events
  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  // Heartbeat probe — verifies actual backend reachability (navigator.onLine can lie)
  useEffect(() => {
    let cancelled = false;
    const probe = async () => {
      try {
        await axios.get(`${API_URL}/api/health`, { timeout: 4000 });
        if (!cancelled) setIsOnline(true);
      } catch {
        if (!cancelled) setIsOnline(false);
      }
    };
    probe();
    const t = setInterval(probe, 15000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Refresh pending count whenever outlet changes
  const refreshPendingCount = useCallback(async (oid = outletId) => {
    if (!oid) {
      setPendingCount(0);
      return;
    }
    const c = await countPendingByOutlet(oid);
    setPendingCount(c);
  }, [outletId]);

  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  // Cache fresh menu data (called from AppContext on every successful fetch)
  const saveMenuCache = useCallback(async (oid, payload) => {
    try {
      await cacheMenuData(oid, payload);
    } catch (e) {
      console.warn('Menu cache write failed', e);
    }
  }, []);

  const loadMenuCache = useCallback(async (oid) => {
    try {
      return await getCachedMenuData(oid);
    } catch (e) {
      console.warn('Menu cache read failed', e);
      return null;
    }
  }, []);

  // Submit an order — online: POST directly, offline: enqueue
  const submitOrder = useCallback(async (orderPayload) => {
    if (effectiveOnline) {
      const res = await axios.post(`${API_URL}/api/orders`, orderPayload, { withCredentials: true });
      return { online: true, order: res.data };
    }
    const queued = await enqueuePendingOrder(orderPayload);
    await refreshPendingCount(orderPayload.outlet_id);
    toast.success(`Order queued offline (#${queued.local_id}). Will sync when online.`);
    return { online: false, queued };
  }, [effectiveOnline, refreshPendingCount]);

  // Flush queued orders to backend
  const flushQueue = useCallback(async () => {
    if (syncingRef.current || !effectiveOnline) return;
    const all = await listPendingOrders();
    if (all.length === 0) return;
    syncingRef.current = true;
    setIsSyncing(true);
    let successCount = 0;
    let failCount = 0;
    for (const entry of all) {
      const { local_id, status, attempts, last_error, queued_at, ...payload } = entry;
      try {
        await axios.post(`${API_URL}/api/orders`, payload, { withCredentials: true });
        await markOrderSynced(local_id);
        successCount += 1;
      } catch (e) {
        await markOrderFailed(local_id, e?.message || 'unknown');
        failCount += 1;
      }
    }
    syncingRef.current = false;
    setIsSyncing(false);
    await refreshPendingCount();
    if (successCount > 0) toast.success(`Synced ${successCount} queued order${successCount > 1 ? 's' : ''}`);
    if (failCount > 0) toast.error(`${failCount} order${failCount > 1 ? 's' : ''} failed to sync`);
  }, [effectiveOnline, refreshPendingCount]);

  // Auto-flush whenever we come back online
  useEffect(() => {
    if (effectiveOnline) flushQueue();
  }, [effectiveOnline, flushQueue]);

  const value = {
    isOnline: effectiveOnline,
    rawOnline: isOnline,
    simulatedOffline,
    setSimulatedOffline,
    isSyncing,
    pendingCount,
    setOutletId,
    saveMenuCache,
    loadMenuCache,
    submitOrder,
    flushQueue,
    refreshPendingCount,
  };

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
};
